import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { api } from "../lib/api";
import { useSessionSocket, useCountdown } from "../lib/ws";
import { AwardBig } from "../components/Awards";

export default function ProjectorMode() {
  const { code } = useParams();
  const [session, setSession] = useState(null);
  const [results, setResults] = useState(null);
  const [awards, setAwards] = useState([]);
  const [awardIndex, setAwardIndex] = useState(0);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get(`/public/session/${code}`);
      setSession(data);
      if (data.current_challenge?.id) {
        const { data: r } = await api.get(`/sessions/${data.id}/results/${data.current_challenge.id}`);
        setResults(r);
      } else {
        setResults(null);
      }
      if (data.status === "finished" && data.awards_reveal_mode === "public") {
        const { data: a } = await api.get(`/sessions/${data.id}/awards`);
        setAwards(a.awards || []);
      } else {
        setAwards([]);
      }
    } catch (e) {
      console.error(e);
    }
  }, [code]);

  useEffect(() => {
    refresh();
    const interval = setInterval(refresh, 3000);
    return () => clearInterval(interval);
  }, [refresh]);

  useSessionSocket(session?.id, () => refresh());

  // Auto-rotate awards every 6s when ceremony is active
  useEffect(() => {
    if (awards.length === 0 || session?.status !== "finished") return;
    const t = setInterval(() => {
      setAwardIndex((i) => (i + 1) % awards.length);
    }, 6000);
    return () => clearInterval(t);
  }, [awards.length, session?.status]);

  // Keyboard navigation for ceremony
  useEffect(() => {
    const onKey = (e) => {
      if (awards.length === 0) return;
      if (e.key === "ArrowRight" || e.key === " ") {
        e.preventDefault();
        setAwardIndex((i) => (i + 1) % awards.length);
      }
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        setAwardIndex((i) => (i - 1 + awards.length) % awards.length);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [awards.length]);

  // Reset award index if the array length shrinks unexpectedly
  useEffect(() => {
    if (awardIndex >= awards.length && awards.length > 0) setAwardIndex(0);
  }, [awards.length, awardIndex]);

  const remaining = useCountdown(
    session?.current_challenge_started_at,
    session?.current_challenge_duration,
    session?.current_challenge_status
  );

  if (!session) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center text-white font-heading text-3xl">
        Načítám projektor...
      </div>
    );
  }

  const cur = session.current_challenge;
  const totalResponses = results?.total_responses || 0;
  const totalParticipants = session.participants_count;

  return (
    <div className="min-h-screen bg-[#050505] text-white relative overflow-hidden grain">
      {/* Gradient accents */}
      <div className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full bg-brand-orange/20 blur-3xl" />
      <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full bg-brand-cyan/20 blur-3xl" />

      <div className="relative z-10 min-h-screen flex flex-col p-12 lg:p-20">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md bg-brand-orange" />
            <div>
              <div className="font-heading font-black text-xl tracking-tight">digitální most</div>
              <div className="text-sm text-white/50">{session.workshop_title}</div>
            </div>
          </div>
          <div className="flex items-center gap-6">
            <div className="text-right">
              <div className="text-xs text-white/50 uppercase tracking-widest">Session kód</div>
              <div className="font-mono font-black text-4xl">{session.code}</div>
            </div>
            <div className="text-right">
              <div className="text-xs text-white/50 uppercase tracking-widest">Připojeno</div>
              <div className="font-heading font-black text-4xl">{totalParticipants}</div>
            </div>
          </div>
        </div>

        {/* Main content */}
        <div className="flex-1 flex items-center">
          {!cur && session.status !== "finished" && session.projector_media && (
            <MediaBig media={session.projector_media} />
          )}
          {!cur && session.status !== "finished" && !session.projector_media && <WaitingBig code={session.code} />}
          {session.status === "finished" && session.awards_reveal_mode !== "public" && (
            <PrivateAwardsBig />
          )}
          {session.status === "finished" && session.awards_reveal_mode === "public" && awards.length === 0 && <FinishedBig />}
          {session.status === "finished" && session.awards_reveal_mode === "public" && awards.length > 0 && (
            <div className="w-full">
              <AwardBig key={awards[awardIndex].id} award={awards[awardIndex]} />
              <div className="mt-12 flex items-center justify-center gap-2">
                {awards.map((a, i) => (
                  <button
                    key={a.id}
                    onClick={() => setAwardIndex(i)}
                    className={`h-2 rounded-full transition-all ${
                      i === awardIndex ? "w-12 bg-brand-orange" : "w-2 bg-white/30"
                    }`}
                    data-testid={`award-dot-${a.id}`}
                    aria-label={a.title}
                  />
                ))}
              </div>
              <div className="mt-4 text-center text-white/40 text-sm uppercase tracking-widest">
                Ocenění {awardIndex + 1} / {awards.length} · ←/→ pro navigaci
              </div>
            </div>
          )}
          {cur && session.status !== "finished" && (
            <ChallengeBig
              challenge={cur}
              topicContext={session.current_topic_context}
              remaining={remaining}
              status={session.current_challenge_status}
              results={results}
              totalParticipants={totalParticipants}
              totalResponses={totalResponses}
            />
          )}
        </div>

        {/* Footer */}
        <div className="text-xs text-white/30 uppercase tracking-[0.3em]">
          Projektor · digitální most · připoj se na URL a zadej kód {session.code}
        </div>
      </div>
    </div>
  );
}

function MediaBig({ media }) {
  const isEmbed = media.type === "video" || media.type === "iframe";
  if (isEmbed && media.url) {
    return (
      <div className="w-full" data-testid="projector-media">
        <div className="text-brand-cyan font-bold uppercase tracking-[0.3em] text-sm mb-4">
          {media.title || "Video"}
        </div>
        <div className="relative w-full rounded-2xl overflow-hidden border border-white/10 bg-black" style={{ aspectRatio: "16 / 9" }}>
          <iframe
            src={media.url}
            title={media.title || "media"}
            allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture; fullscreen"
            allowFullScreen
            className="absolute inset-0 w-full h-full"
          />
        </div>
      </div>
    );
  }
  // text / intro / quiz_intro → render big on a colored panel
  const bg = media.background_color || "#111111";
  return (
    <div className="w-full" data-testid="projector-media">
      <div
        className="w-full rounded-2xl border border-white/10 p-16 min-h-[55vh] flex flex-col justify-center"
        style={{ background: bg }}
      >
        <h1 className="font-heading font-black text-[clamp(2.5rem,7vw,6rem)] leading-[0.9] tracking-tighter text-white">
          {media.title}
        </h1>
        {media.subtitle && (
          <div className="mt-4 text-2xl md:text-3xl text-white/80">{media.subtitle}</div>
        )}
        {media.text && (
          <div className="mt-8 text-2xl md:text-3xl text-white/90 whitespace-pre-line leading-relaxed max-w-5xl">
            {media.text}
          </div>
        )}
        {media.description && !media.text && (
          <div className="mt-6 text-2xl text-white/80">{media.description}</div>
        )}
      </div>
    </div>
  );
}

function WaitingBig({ code }) {
  return (
    <div className="w-full">
      <div className="text-brand-orange font-bold uppercase tracking-[0.3em] text-sm mb-4">Lobby</div>
      <h1 className="font-heading font-black text-[clamp(3rem,10vw,10rem)] leading-[0.85] tracking-tighter">
        Připoj se.<br />
        <span className="text-brand-orange">Čekáme na tebe.</span>
      </h1>
      <div className="mt-12 flex items-center gap-6">
        <div className="bg-white text-black font-mono font-black text-[clamp(3rem,8vw,7rem)] leading-none px-8 py-4 rounded-xl">
          {code}
        </div>
        <div className="text-white/60 text-xl max-w-md">Otevři telefon, přejdi na adresu a zadej tenhle kód.</div>
      </div>
    </div>
  );
}

function FinishedBig() {
  return (
    <div className="w-full text-center">
      <h1 className="font-heading font-black text-[clamp(3rem,12vw,12rem)] leading-[0.85] tracking-tighter">
        Díky.<br />
        <span className="text-brand-cyan">To bylo skvělé.</span>
      </h1>
    </div>
  );
}

function PrivateAwardsBig() {
  return (
    <div className="w-full text-center max-w-3xl mx-auto">
      <div className="text-brand-cyan font-bold uppercase tracking-[0.4em] text-sm mb-4">
        Soukromý mód
      </div>
      <h1 className="font-heading font-black text-[clamp(2.5rem,8vw,7rem)] leading-[0.9] tracking-tighter">
        Každý si <span className="text-brand-orange">svoje</span> uvidí na svém telefonu.
      </h1>
      <p className="mt-6 text-xl md:text-2xl text-white/70 leading-relaxed">
        Lektor může ceremoniál spustit na projektoru tlačítkem „Spustit ceremoniál" v kontrolním panelu.
      </p>
      <div className="mt-12 text-sm text-white/40 uppercase tracking-widest">
        Diskutujte o ocenění v třídě, ne na obrazovce ⚡
      </div>
    </div>
  );
}

function ChallengeBig({ challenge, topicContext, remaining, status, results, totalParticipants, totalResponses }) {
  const warn = remaining <= 10 && status === "running";
  return (
    <div className="w-full grid grid-cols-1 xl:grid-cols-[1fr_420px] gap-12 items-start">
      <div>
        <div className="text-brand-cyan font-bold uppercase tracking-[0.3em] text-sm mb-2">
          {status === "paused" ? "⏸ Pauza" : "Běžící výzva"}
        </div>
        {topicContext && (
          <div className="text-white/60 text-lg md:text-xl mb-4" data-testid="projector-topic">
            <span className="text-brand-orange font-black tracking-tight">Téma {topicContext.topic_index}/{topicContext.total_topics}</span>
            <span className="mx-3 text-white/30">·</span>
            <span className="font-bold">{topicContext.topic_title}</span>
            <span className="mx-3 text-white/30">·</span>
            <span>Otázka {topicContext.question_index}/{topicContext.total_questions}</span>
          </div>
        )}
        <h1 className="font-heading font-black text-[clamp(2.5rem,8vw,7rem)] leading-[0.88] tracking-tighter">
          {challenge.title}
        </h1>
        <p className="mt-6 text-xl md:text-2xl text-white/70 max-w-3xl leading-relaxed">
          {challenge.projector_text || challenge.context}
        </p>

        {/* Choices preview (for vote / quick_decision) */}
        {["vote", "quick_decision"].includes(challenge.type) && (
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-3 max-w-4xl">
            {(challenge.choices || []).map((c, i) => {
              const count = results?.counts_by_choice?.[c.id] || 0;
              const pct = totalResponses ? (count / totalResponses) * 100 : 0;
              return (
                <div key={c.id} className="bg-[#171717] border border-white/10 rounded-xl p-5">
                  <div className="flex items-center justify-between mb-2">
                    <div className="font-bold text-lg">{String.fromCharCode(65 + i)}. {c.label}</div>
                    <div className="font-mono font-bold text-brand-orange">{count}</div>
                  </div>
                  <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                    <div className="h-full bg-brand-orange" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="space-y-4">
        {/* Timer */}
        <div className={`rounded-2xl p-8 text-center ${warn ? "bg-brand-pink text-white animate-pulse" : "bg-[#171717] border border-white/10"}`}>
          <div className="text-sm uppercase tracking-widest text-white/60 mb-1">Čas</div>
          <div className="font-mono font-black text-8xl leading-none tabular-nums">
            {Math.floor(remaining / 60)}:{String(remaining % 60).padStart(2, "0")}
          </div>
        </div>

        {/* Progress */}
        <div className="bg-[#171717] border border-white/10 rounded-2xl p-6">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm uppercase tracking-widest text-white/60">Odpovědělo</div>
            <div className="font-mono font-black text-2xl">{totalResponses} / {totalParticipants}</div>
          </div>
          <div className="h-3 bg-white/10 rounded-full overflow-hidden">
            <div
              className="h-full bg-brand-cyan transition-all"
              style={{ width: `${totalParticipants ? (totalResponses / totalParticipants) * 100 : 0}%` }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
